import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { UrgencyBadge, urgencyBorder, TypeBadge } from "@/components/Badges";
import { FileText, Building2, HeartPulse, XCircle, AlertTriangle, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card data-testid={testId} className="animate-fade-up p-5 transition-transform hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg", accent)}>
          <Icon className="h-4.5 w-4.5" />
        </div>
      </div>
      <div className="mt-3 font-display text-3xl font-bold">{value}</div>
    </Card>
  );
}

function BucketCard({ icon: Icon, label, value, cls, testId }) {
  return (
    <Card data-testid={testId} className={cn("border-l-4 p-4", cls)}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-2 font-display text-2xl font-bold">{value}</div>
    </Card>
  );
}

export default function Dashboard() {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["dashboard"], queryFn: async () => (await api.get("/dashboard")).data });

  if (isLoading || !data) {
    return <div className="flex h-64 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-renco-gold border-t-transparent" /></div>;
  }

  const b = data.buckets;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("overview")}</h1>
        <p className="mt-1 text-muted-foreground">{t("overviewSub")}</p>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-documents" icon={FileText} label={t("totalDocuments")} value={data.totals.documents} accent="bg-renco-navy/10 text-renco-navy" />
        <StatCard testId="stat-companies" icon={Building2} label={t("totalCompanies")} value={data.totals.companies} accent="bg-renco-gold/20 text-renco-gold" />
        <StatCard testId="stat-health" icon={HeartPulse} label={t("healthIndex")} value={`${data.health_index}%`} accent="bg-emerald-100 text-emerald-600" />
        <StatCard testId="stat-attention" icon={AlertTriangle} label={t("expired")} value={b.expired} accent="bg-rose-100 text-rose-600" />
      </div>

      {/* Urgency buckets */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <BucketCard testId="bucket-expired" icon={XCircle} label={t("expired")} value={b.expired} cls={urgencyBorder.expired} />
        <BucketCard testId="bucket-critical" icon={AlertTriangle} label={t("critical")} value={b.critical} cls={urgencyBorder.critical} />
        <BucketCard testId="bucket-warning" icon={Clock} label={t("warning")} value={b.warning} cls={urgencyBorder.warning} />
        <BucketCard testId="bucket-safe" icon={ShieldCheck} label={t("safe")} value={b.safe} cls={urgencyBorder.safe} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Expired / action */}
        <Card className="p-5" data-testid="expired-panel">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">{t("expiredDocs")}</h3>
            <XCircle className="h-4 w-4 text-rose-600" />
          </div>
          <div className="space-y-2">
            {data.expired.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">{t("noExpired")}</p>}
            {data.expired.map((d) => (
              <Link to="/documents" key={d.id} data-testid={`expired-item-${d.id}`} className="flex items-center justify-between rounded-lg border border-rose-100 bg-rose-50/50 px-3 py-2.5 transition-colors hover:bg-rose-50 dark:border-rose-900/40 dark:bg-rose-950/20">
                <div className="min-w-0">
                  <div className="truncate font-medium">{d.title}</div>
                  <div className="text-xs text-muted-foreground">{d.company_name} · <TypeBadge type={d.type} /></div>
                </div>
                <span className="ml-2 shrink-0 text-xs font-semibold text-rose-600">{Math.abs(d.days_to_expiry)} {t("daysOverdue")}</span>
              </Link>
            ))}
          </div>
        </Card>

        {/* Upcoming */}
        <Card className="p-5" data-testid="upcoming-panel">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-display text-lg font-semibold">{t("upcomingRenewals")}</h3>
            <Clock className="h-4 w-4 text-amber-600" />
          </div>
          <div className="space-y-2">
            {data.upcoming.length === 0 && <p className="py-6 text-center text-sm text-muted-foreground">{t("noUpcoming")}</p>}
            {data.upcoming.map((d) => (
              <Link to="/documents" key={d.id} data-testid={`upcoming-item-${d.id}`} className="flex items-center justify-between rounded-lg border px-3 py-2.5 transition-colors hover:bg-secondary">
                <div className="min-w-0">
                  <div className="truncate font-medium">{d.title}</div>
                  <div className="text-xs text-muted-foreground">{d.company_name}</div>
                </div>
                <div className="ml-2 flex shrink-0 items-center gap-2">
                  <span className="text-xs font-medium text-muted-foreground">{d.days_to_expiry} {t("daysLeft")}</span>
                  <UrgencyBadge urgency={d.urgency} />
                </div>
              </Link>
            ))}
          </div>
        </Card>
      </div>

      {/* By company */}
      <Card className="p-5" data-testid="by-company-panel">
        <h3 className="mb-4 font-display text-lg font-semibold">{t("byCompany")}</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.by_company.map((c) => (
            <Link to={`/documents`} key={c.company_id} data-testid={`company-summary-${c.company_id}`} className="group flex items-center justify-between rounded-lg border bg-card px-4 py-3 transition-colors hover:border-renco-gold">
              <div className="min-w-0">
                <div className="truncate font-medium">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.total} {t("docs")}{c.attention > 0 && <span className="ml-2 text-rose-600">· {c.attention} {t("attention")}</span>}</div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
            </Link>
          ))}
          {data.by_company.length === 0 && <p className="col-span-full py-4 text-center text-sm text-muted-foreground">{t("noCompanies")}</p>}
        </div>
      </Card>
    </div>
  );
}
